import { Fragment, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";

import useStore2 from "../../store/hook";
import { useStoreBase } from "../../store";
import { formatXypDate, stringValue } from "../../utils/helper";
import Button from "../../base-components/Button";
import Input from "../../base-components/Input";
import Typography from "../../base-components/Typography";
import PageHeader from "../../components/PageHeader";
import Card from "../../components/Card";
import Tile from "../../components/Tile";
import Skeleton from "../../base-components/Skeleton";
import { ReactComponent as Check } from "../../assets/icons/check-circle-2-1.svg";
import { ReactComponent as Car } from "../../assets/icons/car-1.svg";
import XypService from "../../services/xyp/service";

interface XypVehicleInfoProps {
  plate_number: string;
}

const XypVehicleInfo = () => {
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      plate_number: "",
      vin: "",
    },
  });

  const [loadingVehicle, setLoadingVehicle] = useState(false);
  const vehicleInfo = useStore2.useVehicleInfo();
  const setVehicleInfo = useStore2.useSetVehicleInfo();
  const setName = useStore2.useSetName();

  // Hur-s tatah
  const onSubmitXyp = (data: XypVehicleInfoProps) => {
    if (data.plate_number.length === 7) {
      setLoadingVehicle(true);
      XypService.getVehicle(data.plate_number)
        .then((xypResp) => {
          if (xypResp?.resultCode === 0) {
            // Моделийн шалгалтгүйгээр шууд эвдрэл таних урсгал руу оруулна
            setVehicleInfo({
              ...xypResp?.response,
              importDate: formatXypDate(xypResp?.response?.importDate),
              vin: xypResp?.response?.cabinNumber,
              make: xypResp?.response?.markName,
              model: xypResp?.response?.modelName,
              color: xypResp?.response?.colorName,
              year: xypResp?.response?.buildYear,
            });
          }
        })
        .catch(() => {})
        .finally(() => setLoadingVehicle(false));
    }
  };

  const onFinish = () => {
    if (!!vehicleInfo?.plateNumber) {
      setName(vehicleInfo.plateNumber);
      navigate("/damages");
    } else {
      useStoreBase.setState({
        notification: { message: "Улсын дугаар оруулаагүй байна.", type: "warning" },
      });
    }
  };

  const saveXypInfo = () => {
    onFinish();
  };

  return (
    <Fragment>
      <PageHeader title="Тээврийн хэрэгслийн мэдээлэл" backTo="/victim" />
      <Typography size="md" className="text-black-64">
        Хохирогчийн тээврийн хэрэгслийн мэдээллийг оруулна уу
      </Typography>
      <Card>
        <form onSubmit={handleSubmit(onSubmitXyp)} className={"space-y-4"}>
          <div>
            <Input
              label="Улсын дугаар"
              className="w-full"
              disabled={!!watch("vin", "")}
              {...register("plate_number", {
                required: !watch("vin", ""),
              })}
            />
            {errors?.plate_number?.type === "required" && (
              <p className="text-xs text-error font-semibold mt-1 ml-1">
                Улсын дугаараа оруулна уу.
              </p>
            )}
            {errors?.plate_number?.type === "pattern" && (
              <p className="text-error text-xs pl-2 mt-1">
                Улсын дугаар буруу байна.
              </p>
            )}
          </div>
          <Button
            className="w-full"
            disabled={!watch("plate_number", "") && !watch("vin", "")}
          >
            ХУР-аас татах
          </Button>
        </form>
      </Card>

      {loadingVehicle && (
        <Card>
          <div className="flex items-center space-x-3">
            <Skeleton variant="circle" className="h-8 w-8" />
            <Skeleton variant="text" className="w-40" />
          </div>
          <div className="grid grid-cols-2 gap-4 gap-y-3 mt-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="space-y-1.5">
                <Skeleton variant="text" className="w-16" />
                <Skeleton variant="text" className="w-24 h-4" />
              </div>
            ))}
          </div>
        </Card>
      )}

      {vehicleInfo && (
        <Fragment>
          <Card>
            <header className="flex items-center space-x-3">
              <div className="inline-flex justify-center items-center h-8 w-8 rounded-full bg-secondary-12">
                <Car fill="#E3A13A" className="w-4 h-4" />
              </div>
              <Typography variant="Title" size="md">
                Тээврийн хэрэгслийн мэдээлэл
              </Typography>
            </header>
            <div className="py-2">
              <Tile
                field={"Арлын дугаар"}
                value={stringValue(vehicleInfo?.vin)}
              />
            </div>
            <div className="grid grid-cols-2 gap-4 gap-y-2">
              <Tile
                field={"Улсын дугаар"}
                value={stringValue(vehicleInfo?.plateNumber)}
              />
              <Tile
                field={"Үйлдвэрлэгч"}
                value={stringValue(vehicleInfo?.make)}
              />
              <Tile field={"Модел"} value={stringValue(vehicleInfo?.model)} />
              <Tile field={"Өнгө"} value={stringValue(vehicleInfo?.color)} />
              <Tile
                field={"Үйлдвэрлэсэн он"}
                value={stringValue(vehicleInfo?.year)}
                className="border-none"
              />
              <Tile
                field={"Орж ирсэн огноо"}
                value={stringValue(vehicleInfo?.importDate)}
                className="border-none"
              />
            </div>
          </Card>
          <div className="mt-4 flex space-x-4 py-4">
            <Button
              icon={<Check fill="#fff" className="w-[18px] h-[18px]" />}
              onClick={() => saveXypInfo()}
              className="w-full"
            >
              Баталгаажуулах
            </Button>
          </div>
        </Fragment>
      )}
    </Fragment>
  );
};

export default XypVehicleInfo;
